import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Chip,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Stars as RarityIcon,
  EmojiEvents as TierIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Assignment as CriteriaIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Button from '../UIKit/Button';
import { useTranslation } from 'react-i18next';

const ViewBadgeDialog = ({
  open,
  onClose,
  badge,
  criteriaDisplay,
  handleOpenEdit,
  getRarityColor,
  renderCriteriaDescription,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['badges', 'common']);

  if (!badge) return null;

  // Получаем цвет редкости для использования в градиентах и акцентах
  const rarityColor = getRarityColor(badge.rarity);
  const lightRarityColor = alpha(rarityColor, 0.1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundImage: `radial-gradient(circle at top right, ${lightRarityColor}, transparent 70%), 
                           radial-gradient(circle at bottom left, ${lightRarityColor}, transparent 70%),
                           linear-gradient(135deg, transparent, ${alpha(
                             rarityColor,
                             0.15
                           )} 50%, transparent 100%)`,
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Avatar
          src={badge.image}
          alt={badge.name}
          sx={{
            width: 40,
            height: 40,
            mr: 2,
            border: `2px solid ${rarityColor}`,
            boxShadow: `0 0 10px ${alpha(rarityColor, 0.5)}`,
          }}
        />
        <Typography
          variant="h5"
          component="div"
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          {badge.name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 0 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 180,
                  height: 180,
                  mb: 4,
                  borderRadius: '50%',
                  p: '5px',
                  background: `linear-gradient(135deg, ${rarityColor}, ${alpha(
                    rarityColor,
                    0.3
                  )})`,
                  boxShadow: `0 10px 25px ${alpha(rarityColor, 0.4)}`,
                }}
              >
                <Avatar
                  src={badge.image}
                  alt={badge.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: `4px solid ${theme.palette.background.paper}`,
                    boxShadow: 'inset 0 0 15px rgba(0,0,0,0.1)',
                    transition: 'transform 0.4s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(5deg)',
                    },
                  }}
                />
              </Box>

              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CriteriaIcon
                    sx={{ mr: 1, color: alpha(rarityColor, 0.8) }}
                  />
                  <Typography variant="h6" component="h3">
                    {t('badges:view.mainInfo')}
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '5px',
                      height: '100%',
                      background: rarityColor,
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 0,
                      lineHeight: 1.7,
                      textAlign: 'justify',
                      pl: 1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {badge.description}
                  </Typography>
                </Paper>

                <Grid container spacing={2.5}>
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <CategoryIcon
                          fontSize="small"
                          sx={{ mr: 1, color: 'primary.main' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t('badges:view.category')}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(`badges:badge.category.${badge.category}`)}
                        sx={{
                          mt: 'auto',
                          borderRadius: '8px',
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <RarityIcon
                          fontSize="small"
                          sx={{ mr: 1, color: rarityColor }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t('badges:view.rarity')}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(`badges:badge.rarity.${badge.rarity}`)}
                        sx={{
                          mt: 'auto',
                          borderRadius: '8px',
                          fontWeight: 600,
                          bgcolor: alpha(rarityColor, 0.1),
                          color: rarityColor,
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <TierIcon
                          fontSize="small"
                          sx={{ mr: 1, color: theme.palette.warning.main }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t('badges:view.tier')}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(`badges:badge.tier.${badge.tier}`)}
                        sx={{
                          mt: 'auto',
                          borderRadius: '8px',
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          color: theme.palette.warning.main,
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        {badge.isSecret ? (
                          <LockIcon
                            fontSize="small"
                            sx={{ mr: 1, color: theme.palette.error.main }}
                          />
                        ) : (
                          <UnlockIcon
                            fontSize="small"
                            sx={{ mr: 1, color: theme.palette.success.main }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {t('badges:view.isSecret')}
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          badge.isSecret
                            ? t('common:status.yes')
                            : t('common:status.no')
                        }
                        sx={{
                          mt: 'auto',
                          borderRadius: '8px',
                          fontWeight: 500,
                          bgcolor: badge.isSecret
                            ? alpha(theme.palette.error.main, 0.1)
                            : alpha(theme.palette.success.main, 0.1),
                          color: badge.isSecret
                            ? theme.palette.error.main
                            : theme.palette.success.main,
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                background: alpha(theme.palette.background.paper, 0.3),
                backdropFilter: 'blur(100px)',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                boxShadow: `0 5px 15px ${alpha(
                  theme.palette.common.black,
                  0.05
                )}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CriteriaIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                <Typography variant="h6" component="h3">
                  {t('badges:view.criteria')}
                </Typography>
              </Box>

              {criteriaDisplay ? (
                <Box
                  sx={{
                    mt: 2,
                  }}
                >
                  {criteriaDisplay.operator ? (
                    // Составное условие
                    <Box>
                      <Box
                        sx={{
                          py: 1,
                          px: 2,
                          mb: 2,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          borderRadius: 2,
                          border: `1px solid ${alpha(
                            theme.palette.info.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                            color: theme.palette.info.main,
                          }}
                        >
                          {t('badges:view.mustMeet')}{' '}
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {criteriaDisplay.operator === 'AND'
                              ? t('badges:view.allOf')
                              : t('badges:view.anyOf')}
                          </Box>
                        </Typography>
                      </Box>
                      <List sx={{ pl: 0 }}>
                        {criteriaDisplay.conditions &&
                          criteriaDisplay.conditions.map((condition, idx) => (
                            <ListItem
                              key={idx}
                              sx={{
                                py: 1,
                                px: 2,
                                mb: 1.5,
                                borderRadius: 2,
                                bgcolor: alpha(
                                  theme.palette.background.paper,
                                  0.5
                                ),
                                border: `1px solid`,
                                borderColor: 'divider',
                                '&:hover': {
                                  bgcolor: alpha(
                                    theme.palette.background.paper,
                                    0.8
                                  ),
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {idx + 1}
                                </Box>
                              </ListItemIcon>
                              <ListItemText
                                primary={renderCriteriaDescription(condition)}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  lineHeight: 1.6,
                                  sx: {
                                    fontWeight: '400',
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.6,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        border: `1px solid`,
                        borderColor: 'divider',
                      }}
                    >
                      {renderCriteriaDescription(criteriaDisplay)}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    color: theme.palette.text.secondary,
                    bgcolor: alpha(theme.palette.background.default, 0.3),
                    borderRadius: 2,
                    mt: 2,
                  }}
                >
                  <Typography>
                    {t('badges:view.criteriaUnavailable')}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 4 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <PreviewIcon
                    sx={{ mr: 1, color: theme.palette.success.main }}
                  />
                  <Typography variant="h6" component="h3">
                    {t('badges:view.previewSizes')}
                  </Typography>
                </Box>

                <Grid
                  container
                  spacing={2}
                  sx={{ mt: 1, justifyContent: 'center' }}
                >
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.3),
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Avatar
                        src={badge.image}
                        alt={badge.name}
                        sx={{
                          width: 24,
                          height: 24,
                          border: `1px solid ${rarityColor}`,
                          mb: 1,
                        }}
                      />
                      <Typography variant="caption" display="block">
                        24x24
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.3),
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Avatar
                        src={badge.image}
                        alt={badge.name}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `2px solid ${rarityColor}`,
                          mb: 1,
                        }}
                      />
                      <Typography variant="caption" display="block">
                        48x48
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.3),
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Avatar
                        src={badge.image}
                        alt={badge.name}
                        sx={{
                          width: 96,
                          height: 96,
                          border: `3px solid ${rarityColor}`,
                          mb: 1,
                        }}
                      />
                      <Typography variant="caption" display="block">
                        96x96
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onClose();
            handleOpenEdit(badge);
          }}
          startIcon={<EditIcon />}
          sx={{
            bgcolor: rarityColor,
            '&:hover': {
              bgcolor: alpha(rarityColor, 0.8),
            },
          }}
        >
          {t('common:actions.edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewBadgeDialog;
